/** Request-local, content-free diagnostics. These records are never response data. */
export type ContextAssemblySurface = "coach_chat" | "leader_chat" | "session_prep" | "reflection_generation" | "leader_sense_making" | "coach_sense_making";
export type EvidenceVisibilityScope = "coach_only" | "leader_private" | "leader_safe" | "shared";
export type ContextSourceReason = "complete" | "no_rows" | "visibility_filtered" | "lane_withheld" | "query_failed" | "partial_query_failure" | "invalid_payload" | "fast_path_snapshot" | "not_queried";
export interface ContextSourceReceipt {
  kind: string;
  status: "ready" | "empty" | "unavailable" | "degraded" | "skipped";
  row_count: number;
  reason_code: ContextSourceReason;
}
export interface ContextBinding { kind:'framework_pack'|'context'|'source'|'correction'|'practice_selection'; id:string; revision:string }
export interface ContextSelectionManifest {
  bindings?: ContextBinding[];
  final_evidence: Array<{ source_id: string; visibility_scope: EvidenceVisibilityScope }>;
  truncations: Array<{ section: string; count: number; cap: number }>;
  unattributed_selections: Array<{ section: string; count: number }>;
}
export interface PromptSelection {
  bind?(binding: ContextBinding): void;
  ids(section: string, refs: readonly { source_id: string; visibility_scope: EvidenceVisibilityScope }[]): void;
  cap(section: string, eligibleCount: number, limit: number): void;
  unattributed(section: string, renderedCount: number): void;
  manifest: ContextSelectionManifest;
}

export function createPromptSelection(): PromptSelection {
  const manifest: ContextSelectionManifest = { final_evidence: [], truncations: [], unattributed_selections: [] };
  const record = (callback: () => void) => {
    try { callback(); }
    catch {
      // Retain an invalid sentinel for the single emitter to reject without
      // ever passing raw input to a logger or interrupting prompt assembly.
      manifest.final_evidence = [null as unknown as ContextSelectionManifest["final_evidence"][number]];
    }
  };
  return {
    manifest,
    bind(binding) { record(()=>{ (manifest.bindings ??= []).push(binding); }); },
    ids(_section, refs) { record(() => {
      for (const ref of refs) manifest.final_evidence.push({ source_id: ref.source_id, visibility_scope: ref.visibility_scope });
    }); },
    cap(section, eligibleCount, limit) { record(() => {
      if (eligibleCount > limit) manifest.truncations.push({ section, count: eligibleCount - limit, cap: limit });
    }); },
    unattributed(section, renderedCount) { record(() => {
      if (renderedCount > 0) manifest.unattributed_selections.push({ section, count: renderedCount });
    }); },
  };
}

export interface ContextAssemblyReceipt {
  event: "context_assembly_receipt";
  context_bindings?: ContextBinding[];
  schema_version: 1;
  surface: ContextAssemblySurface;
  path: "fast" | "full";
  sources: ContextSourceReceipt[];
  final_evidence_source_ids: string[];
  truncations: ContextSelectionManifest["truncations"];
  unattributed_selections: ContextSelectionManifest["unattributed_selections"];
  evidence_id_coverage: "partial";
  source_coverage: "partial";
}

const labelPattern = /^[a-z][a-z0-9_]{0,63}$/;
const idPattern = /^[A-Za-z0-9][A-Za-z0-9:_-]{0,127}$/;
const reasons: readonly ContextSourceReason[] = ["complete", "no_rows", "visibility_filtered", "lane_withheld", "query_failed", "partial_query_failure", "invalid_payload", "fast_path_snapshot", "not_queried"];
function exact(value: unknown, keys: readonly string[]): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new TypeError("receipt_invalid");
  const row = value as Record<string, unknown>;
  const prototype = Object.getPrototypeOf(row);
  const ownKeys = Reflect.ownKeys(row);
  if ((prototype !== Object.prototype && prototype !== null) || ownKeys.length !== keys.length
    || ownKeys.some(key => typeof key !== "string" || !keys.includes(key))
    || keys.some(key => {
      const descriptor = Object.getOwnPropertyDescriptor(row, key);
      return !descriptor || !descriptor.enumerable || !("value" in descriptor);
    })) throw new TypeError("receipt_invalid");
  const projected: Record<string, unknown> = Object.create(null);
  for (const key of keys) projected[key] = Object.getOwnPropertyDescriptor(row, key)!.value;
  return projected;
}
function bounded(value: unknown, max: number): unknown[] {
  if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) throw new TypeError("receipt_invalid");
  const length = Object.getOwnPropertyDescriptor(value, "length")?.value;
  if (!Number.isSafeInteger(length) || length < 0 || length > max || Reflect.ownKeys(value).length !== length + 1) throw new TypeError("receipt_invalid");
  const projected: unknown[] = [];
  for (let index = 0; index < length; index++) {
    const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
    if (!descriptor || !descriptor.enumerable || !("value" in descriptor)) throw new TypeError("receipt_invalid");
    projected.push(descriptor.value);
  }
  return projected;
}
function label(value: unknown): string {
  if (typeof value !== "string" || !labelPattern.test(value)) throw new TypeError("receipt_invalid");
  return value;
}
function count(value: unknown): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0 || value > 1_000_000) throw new TypeError("receipt_invalid");
  return value;
}
function warnInvalid(): void {
  try { console.warn("[context-receipt] receipt_invalid"); } catch { /* Diagnostics cannot fail a request. */ }
}

/** Validate and project every field before any logger sees it. Never copy raw input. */
export function buildContextAssemblyReceipt(input: unknown): ContextAssemblyReceipt {
  const raw = exact(input, ["surface", "path", "sources", "selection"]);
  if (typeof raw.surface !== "string" || typeof raw.path !== "string"
    || !["coach_chat", "leader_chat", "session_prep", "reflection_generation", "leader_sense_making", "coach_sense_making"].includes(raw.surface)
    || !["fast", "full"].includes(raw.path)) throw new TypeError("receipt_invalid");
  const surface = raw.surface as ContextAssemblySurface;
  const leaderFacing = surface === "leader_chat" || surface === "reflection_generation" || surface === "leader_sense_making";
  const hasBindings = !!raw.selection && typeof raw.selection==='object' && Object.prototype.hasOwnProperty.call(raw.selection,'bindings');
  const selection = exact(raw.selection, ["final_evidence", "truncations", "unattributed_selections", ...(hasBindings?['bindings']:[])]);
  const bindings = hasBindings ? bounded(selection.bindings, 256).map(value=>{
    const row=exact(value,['kind','id','revision']);
    if(!['framework_pack','context','source','correction','practice_selection'].includes(String(row.kind))
      || typeof row.id!=='string' || !idPattern.test(row.id) || typeof row.revision!=='string'
      || !/^[A-Za-z0-9][A-Za-z0-9:_.-]{0,127}$/.test(row.revision))throw new TypeError('receipt_invalid');
    return {kind:row.kind as ContextBinding['kind'],id:row.id,revision:row.revision};
  }) : undefined;
  const evidence = bounded(selection.final_evidence, 512).map(value => {
    const row = exact(value, ["source_id", "visibility_scope"]);
    if (typeof row.source_id !== "string" || !idPattern.test(row.source_id)
      || typeof row.visibility_scope !== "string"
      || !["coach_only", "leader_private", "leader_safe", "shared"].includes(row.visibility_scope)) throw new TypeError("receipt_invalid");
    return { source_id: row.source_id, visibility_scope: row.visibility_scope as EvidenceVisibilityScope };
  });
  const unsafeIds = new Set(evidence.filter(row => leaderFacing ? row.visibility_scope === "coach_only" : row.visibility_scope === "leader_private").map(row => row.source_id));
  const sources = bounded(raw.sources, 256).map(value => {
    const row = exact(value, ["kind", "status", "row_count", "reason_code"]);
    if (typeof row.status !== "string" || !["ready", "empty", "unavailable", "degraded", "skipped"].includes(row.status) || !reasons.includes(row.reason_code as ContextSourceReason)) throw new TypeError("receipt_invalid");
    return { kind: label(row.kind), status: row.status as ContextSourceReceipt["status"], row_count: count(row.row_count), reason_code: row.reason_code as ContextSourceReason };
  });
  const truncations = bounded(selection.truncations, 64).map(value => {
    const row = exact(value, ["section", "count", "cap"]);
    return { section: label(row.section), count: count(row.count), cap: count(row.cap) };
  });
  const unattributed = bounded(selection.unattributed_selections, 128).map(value => {
    const row = exact(value, ["section", "count"]);
    return { section: label(row.section), count: count(row.count) };
  });
  return { event: "context_assembly_receipt", schema_version: 1, surface, path: raw.path as "fast" | "full", sources,
    final_evidence_source_ids: [...new Set(evidence.filter(row => !unsafeIds.has(row.source_id)).map(row => row.source_id))],
    ...(bindings?{context_bindings:bindings.filter(binding=>!unsafeIds.has(binding.id))}:{}),
    truncations, unattributed_selections: unattributed, evidence_id_coverage: "partial", source_coverage: "partial" };
}

/** One emission attempt per assembly. Both validation and logger failures are harmless. */
export function createContextAssemblyReceiptEmitter(logger: (receipt: ContextAssemblyReceipt) => void = receipt => console.log(JSON.stringify(receipt))) {
  let attempted = false;
  return (input: unknown): ContextAssemblyReceipt | null => {
    if (attempted) return null;
    attempted = true;
    try {
      const receipt = buildContextAssemblyReceipt(input);
      logger(receipt);
      return receipt;
    } catch {
      warnInvalid();
      return null;
    }
  };
}
